import { DeviceProtocol } from '@hid/device/device_model'
import { BaseDriver } from './device'
import { DataProfileForDecorate, FirmwareUpdateMessage, ResDeviceState } from '@libs/types/response'
import { DeviceConnectMode, FirmwareUpgradeStatus, MusicCaptureStatus, SpecificItem } from '@libs/types/enum'
import Keyboard from '@sparklinkplayjoy/sdk-keyboard-v2'
import { deviceManager } from '@hid/device/device_manager'
import { jingtaiV2BaseInfo, jingtaiV2ColorInput, jingtaiV2CustomColor, jingtaiV2Data, jingtaiV2ResDefaultLayout, jingtaiV2ResHigherKey, jingtaiV2ResRate, JingTaiV2SetMode } from './data_conversion/jingtai_v2'
import { KK, OutputMode, VirtualMg75Max } from '@hid/define/define'
import { jingtaiV2Magnet } from './magent/jingtai_v2'
import { musicData } from '@libs/music/music'
import { decodeRowColNumber, delay, generateMessageID } from '@libs/utils/tools'
import { fakeSocket } from '@hid/fakesocket/fakesocket'
import { DriverSocketMsgType } from '@libs/types/message'
import { getDefineDevice } from '@hid/define/define_filters'
import { GetDevicesEnbleKeys, HidFromKK } from '@hid/define/define_fn'
import firmware from '@/upgrade/firmware'
import { numberToRgb } from '../../libs/utils/tools'
export class jingtaiV2Driver extends BaseDriver {
  deviceIns = new Keyboard({
    // configs: [{ vendorId, productId, usagePage: 0xffb0, usage: 0x01 }],
    configs: [],
    usage: 0x01,
    usagePage: [0xffb0],
  })
  musicIns: musicData

  declare magnet: jingtaiV2Magnet | undefined

  private id: string = ''

  constructor(protocol: DeviceProtocol) {
    super(protocol)
    this.magnet = new jingtaiV2Magnet(protocol, this)
    this.musicIns = new musicData(this.onMusicData)
  }

  // 由于MG75Max固件版本是1.0.6，幻彩灯效设置为单色常量时，在修改自定义颜色，自定义颜色的G或者B<0x07会导致灯光变灭
  // 因此当固件版本为1.0.6，将预设的红色值进行兼容
  private async fixMG75MaxRedColorForV106(value: { r: number; g: number; b: number }): Promise<{ r: number; g: number; b: number }> {
    const isBetween = await firmware.fwVersionBetween('1.0.6', '1.0.7', [VirtualMg75Max])
    let g = value.g
    let b = value.b
    if (isBetween && g < 0x07 && b < 0x07) {
      g = 0x07
    }
    return { r: value.r, g, b }
  }

  async syncCustomColor() {
    await this.musicIns.musicCapturePause()
    const colorsCustom = this.save.ColorsCustom
    const lightMode = this.save.LightMode

    let setValues: jingtaiV2CustomColor[] = this.initCustomData()

    for (const [kk, color] of colorsCustom) {
      const row = jingtaiV2Data.getRow(kk)
      const col = jingtaiV2Data.getCol(kk)

      const luminance = Math.max(lightMode.Light, 1) / 100

      const { r, g, b } = numberToRgb(color)
      const fixedColor = await this.fixMG75MaxRedColorForV106({ r, g, b })

      setValues[row * 21 + col] = {
        R: Math.round(fixedColor.r * luminance),
        G: Math.round(fixedColor.g * luminance),
        B: Math.round(fixedColor.b * luminance),
        isCustom: color > 0 ? true : false,
      }
    }
    if (colorsCustom.size == 0) {
      for (let index = 0; index < setValues.length; index++) {
        const key = setValues[index]
        key.isCustom = false
      }
    }
    await this.setCustomLighting(setValues)

    // await delay(100)

    // let resetValues = JSON.parse(JSON.stringify(setValues))
    // for (let index = 0; index < resetValues.length; index++) {
    //   const key = resetValues[index]
    //   if (key.R == 0 && key.G == 0 && key.B == 0 && key.isCustom == true) {
    //     key.isCustom = false
    //   }
    //   resetValues[index] = key
    // }
    // await this.setCustomLighting(resetValues)

    await this.musicIns.musicCaptureContinue()
  }

  private initCustomData(isCustom: boolean = false, num: number = 126) {
    let setValues: jingtaiV2CustomColor[] = Array(num).fill({
      R: 0,
      G: 0,
      B: 0,
      isCustom: isCustom,
    })
    return setValues
  }

  private async setCustomLighting(setValues: jingtaiV2CustomColor[] | jingtaiV2CustomColor[][], area: string = 'Keyboard') {
    const customLightingConfig = {
      area: area,
      protocol: 'Custom',
      data: [setValues],
    }
    await this.deviceIns.setLightingCustom(customLightingConfig)
  }

  async init() {
    const devices = await this.deviceIns.getDevices()
    for (const device of devices) {
      if (device.productId) {
        const devID = getDefineDevice(device as unknown as HIDDevice)
        if (deviceManager.deviceID == devID) {
          this.id = device.id
          await this.deviceIns.init(device.id)
          break
        }
      }
    }
  }

  private async checkAndDelHigherKey(kk: KK) {
    const key = (await this.deviceIns.getHigherKey({
      row: jingtaiV2Data.getRow(kk),
      col: jingtaiV2Data.getCol(kk),
    })) as jingtaiV2ResHigherKey
    if (key.mode != JingTaiV2SetMode.NONE) {
      await this.delHigherKeyMode(kk)
    }
  }

  async delHigherKeyMode(kk: KK) {
    await this.deviceIns.setHigherKeyNONE({
      row: jingtaiV2Data.getRow(kk),
      col: jingtaiV2Data.getCol(kk),
      mode: 'NONE',
      data: {
        mode: JingTaiV2SetMode.NONE,
      },
    })
  }

  private async getFNDefaultKeysLayout() {
    const map = new Map<number, number[]>() // row - keyCodes
    for (let index = 0; index < 6; index++) {
      const res = (await this.deviceIns.getDefaultKeyLayout({
        row: index,
        fn: 1,
        system: 0, // 0 表示 Windows 系统
      })) as unknown as jingtaiV2ResDefaultLayout
      map.set(index, res.keyCodes)
    }
    return map
  }

  //  切换配置时，恢复默认键位
  async switchProfileRestoreDefaultKeys() {
    if (this.changeProfileFlag) {
      const fnKeyMap = await this.getFNDefaultKeysLayout()
      const kks = GetDevicesEnbleKeys(deviceManager.deviceID)
      for (const kk of kks.keys()) {
        // 检查并删除高级按键模式
        await this.checkAndDelHigherKey(kk)
        // 恢复普通层按键
        await this.deviceIns.setKeyCode({
          layer: 0,
          row: jingtaiV2Data.getRow(kk),
          col: jingtaiV2Data.getCol(kk),
          keycode: HidFromKK(kk, deviceManager.deviceID),
        })
      }
      // 恢复fn层按键
      fnKeyMap.forEach(async (keyCodes, row) => {
        for (let col = 0; col < keyCodes.length; col++) {
          const kk = (row << 8) | col
          if (kks.has(kk as KK)) {
            await this.deviceIns.setKeyCode({
              layer: 1,
              row: row,
              col: col,
              keycode: keyCodes[col],
            })
          }
        }
      })
      await delay(100)
    }
  }

  async syncKeys() {
    await this.musicIns.musicCapturePause()

    await this.switchProfileRestoreDefaultKeys()

    const keys = this.save.Keys
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      if (key.Input.KK < 0) continue
      if (key.Output.Mode == OutputMode.Key) {
        await this.checkAndDelHigherKey(key.Input.KK)
        await this.deviceIns.setKeyCode(jingtaiV2Data.setKey(key))
      }
    }
    await this.magnet?.clearKME()
    await this.musicIns.musicCaptureContinue()
  }

  async syncMacros() {
    await this.musicIns.musicCapturePause()
    const keys = this.save.Keys
    const macros = this.save.Macros

    for (const item of keys) {
      if (item.Input.KK < 0) continue
      if (item.Output.Mode == OutputMode.Macro) {
        const macroID = item.Output.DataIndex
        const macro = macros.get(macroID)
        if (macro) {
          const macroData = jingtaiV2Data.macroData(macroID, macro)
          const macroMode = jingtaiV2Data.macroMode(macroID, item, macro)

          await this.deviceIns.setMacroMode(macroMode)
          for (let index = 0; index < macroData.length; index++) {
            const data = macroData[index]
            await this.deviceIns.setMacroData(data)
          }

          await this.checkAndDelHigherKey(item.Input.KK)
          const setKey = jingtaiV2Data.setKey(item)
          await this.deviceIns.setKeyCode(setKey)
        }
      }
    }
    await this.magnet?.clearKME()

    await this.musicIns.musicCaptureContinue()
  }

  private async setLightBase(area: string, mode: number, speed: number, luminance: number, direction: number, colorIndex: number = 0) {
    const result = await this.deviceIns.setLightingBase(
      {
        area: area,
        config: 'Base',
        data: {
          open: 'Open',
          mode: mode,
          luminance: luminance,
          speed: speed,
          direction: direction == 0 || direction == 3 ? 'Forward' : 'Backward',
          selectStaticColor: colorIndex,
        },
      },
      'SingleLighting',
    )

    return result
  }

  private async setLightPalette(colors: jingtaiV2ColorInput[], area: string = 'Keyboard') {
    return await this.deviceIns.setLightingPalette({
      area: area,
      config: 'Palette',
      data: {
        staticColors: [
          {
            R: 0,
            G: 0,
            B: 0,
          },
          ...colors,
        ],
      },
    })
  }

  private generateColorPalette(data: [number, number, number, number, number, number, number]) {
    let colors = []
    for (let i = 0; i < data.length; i++) {
      const color = data[i]
      colors.push({
        R: (color >> 16) & 0xff,
        G: (color >> 8) & 0xff,
        B: color & 0xff,
      })
    }
    return colors
  }

  private isNeedSyncCustomLed = false
  async syncLeds() {
    await this.musicIns.musicCapturePause()

    if (this.changeProfileFlag || this.isNeedSyncCustomLed) {
      if (this.isNeedSyncCustomLed) {
        this.isNeedSyncCustomLed = false
      }
      await this.syncCustomColor()
    }
    const lightMode = this.save.LightMode

    if (lightMode.Colors == undefined) {
      throw new Error('lightMode.Colors is undefined')
    }

    await this.setLightPalette(this.generateColorPalette(lightMode.Colors.Data))
    await this.setLightBase('Keyboard', lightMode.Mode, lightMode.Speed, lightMode.Light, lightMode.Direction, lightMode.Colors.Index)

    await this.musicIns.musicCaptureContinue()
  }

  async syncConfig() {
    this.setAllSpecificItem()
  }
  private async setAllSpecificItem() {
    const items = this.save.Items
    for (const [key, item] of items) {
      if (key == SpecificItem.Rate) {
        continue
      }
      await this.setOrReadSpecificItem(key, true, item)
    }
  }

  async syncDynamicColor(): Promise<boolean> {
    const colorsDynamic = this.save.ColorsDynamic

    let state = MusicCaptureStatus.Stop
    if (colorsDynamic.Run) {
      state = MusicCaptureStatus.Start
    }
    return await this.setDynamicColorState(state)
  }

  private async setDynamicColorState(state: MusicCaptureStatus) {
    if (state == MusicCaptureStatus.Stop) {
      this.isNeedSyncCustomLed = true
      await this.closeCustomLed()
    }
    this.musicIns.setColorsDynamic(this.save.ColorsDynamic)
    return await this.musicIns.dynamicColorState(state)
  }

  private async closeCustomLed() {
    // const clearColors = this.initCustomData(true)
    // await this.setCustomLighting(clearColors)
    // await delay(100)

    const setValues = this.initCustomData()
    await this.setCustomLighting(setValues)
    return
  }

  private onMusicData = async (data: number[]) => {
    if (this.save.ColorsDynamic.Run == false) {
      return
    }
    if (data.length > 0) {
      let setValues: jingtaiV2CustomColor[] = Array(126).fill({
        R: 0,
        G: 0,
        B: 0,
        isCustom: false,
      })

      for (let index = 0; index < data.length; index += 3) {
        let keyIndex = index / 3
        const row = Math.floor(keyIndex % 6)
        const col = Math.floor(keyIndex / 6)
        setValues[row * 21 + col] = {
          R: data[index],
          G: data[index + 1],
          B: data[index + 2],
          isCustom: true,
        }
      }
      await this.setCustomLighting(setValues)
    }
  }

  async reset() {
    await this.deviceIns.GFSRestore(0)
  }
  async getDeviceState(): Promise<ResDeviceState> {
    return {
      ConnectType: DeviceConnectMode.M_USB,
      IsConnect: true,
      IsSleep: false,
    }
  }

  async getVersion() {
    await this.musicIns.musicCapturePause()
    const info = (await this.deviceIns.getDevicesInfo()) as jingtaiV2BaseInfo
    await this.musicIns.musicCaptureContinue()
    return info.appVersion
  }

  async setOrReadRate(isWrite: boolean, value: number) {
    if (isWrite) {
      let rateStr: string
      if (value >= 1000) {
        rateStr = `R${value / 1000}KHz`
      } else {
        rateStr = `R${value}Hz`
      }
      await this.deviceIns.setRateOfReturn(rateStr)
      return
    }
    const cur: jingtaiV2ResRate = (await this.deviceIns.getRateOfReturn()) as jingtaiV2ResRate
    if (cur.key.startsWith('R') && cur.key.endsWith('KHz')) {
      return parseFloat(cur.key.substring(1, cur.key.length - 3)) * 1000
    } else if (cur.key.startsWith('R') && cur.key.endsWith('Hz')) {
      return parseInt(cur.key.substring(1, cur.key.length - 2))
    }
    return 8
  }

  async setOrReadSpecificItem(item: string, isWrite: boolean, value: any) {
    const item2 = item as SpecificItem

    if (item2 == SpecificItem.SleepTime) {
      if (isWrite) {
        await this.deviceIns.setLightingSleepTime(Math.floor(value / 60))
        return
      }
      const cur = await this.deviceIns.getLightingSleepTime()
      return Math.floor(cur * 60)
    }

    if (item2 == SpecificItem.Rate) {
      const rate = await this.setOrReadRate(isWrite, value)
      return rate
    }
    return null
  }

  async syncDecorationLed() {
    const decorates = this.save.Decorates
    if (this.changeProfileFlag && (decorates == undefined || decorates.length == 0)) {
      return
    }

    if (decorates == undefined || decorates.length == 0) {
      throw new Error('Decorates is undefined')
    }
    await this.musicIns.musicCapturePause()

    for (let i = 0; i < decorates.length; i++) {
      const setName = `Decorate${i + 1}`
      const decorate = decorates[i]

      if (decorate.LightMode.Colors == undefined) {
        throw new Error('lightMode.Colors is undefined')
      }
      await this.setLightPalette(this.generateColorPalette(decorate.LightMode.Colors.Data), setName)
      await this.setLightBase(setName, decorate.LightMode.Mode, decorate.LightMode.Speed, decorate.LightMode.Light, 0, decorate.LightMode.Colors.Index)

      if (decorate.LightLastIsCustom || this.changeProfileFlag) {
        await this.setOrResetDecorationLed(setName, decorate)
        await delay(100)
        await this.setOrResetDecorationLed(setName, decorate, true)
      }
    }

    await this.musicIns.musicCaptureContinue()
  }

  private async setOrResetDecorationLed(setName: string, decorate: DataProfileForDecorate, isReset: boolean = false) {
    const customList: jingtaiV2CustomColor[] = []
    const luminance = Math.max(decorate.LightMode.Light, 1) / 100

    for (const [key, value] of decorate.ColorsCustom) {
      const [_, col] = decodeRowColNumber(key)
      // if (!customList[row]) {
      //   console.log('row', key, row, col)
      //   customList[row] = []
      // }
      // Math.round(((color >> 16) & 0xff) * luminance)

      if (isReset) {
        customList[col] = {
          R: Math.round(((value >> 16) & 0xff) * luminance),
          G: Math.round(((value >> 8) & 0xff) * luminance),
          B: Math.round((value & 0xff) * luminance),
          isCustom: value > 0 ? true : false,
        }
      } else {
        customList[col] = {
          R: Math.round(((value >> 16) & 0xff) * luminance),
          G: Math.round(((value >> 8) & 0xff) * luminance),
          B: Math.round((value & 0xff) * luminance),
          isCustom: true,
        }
      }
    }
    await this.setCustomLighting(customList, setName)
  }

  async pair24g() {
    return 0
  }

  private sendMessage = (data: FirmwareUpdateMessage) => {
    fakeSocket.onmessage({
      data: JSON.stringify({
        ID: generateMessageID(),
        Type: DriverSocketMsgType.MSDeviceUpdateProgress,
        Data: data,
        Error: null,
      }),
    } as MessageEvent)
  }
  private handleUpgradeProgress = (data: UpgradeOptions) => {
    if (data.updateStatus === 'isWriting') {
      this.sendMessage({
        Current: data.current,
        Total: data.total,
        Status: FirmwareUpgradeStatus.Upgrading,
      })
    }
  }
  private handlerUpgrade = async (firmwareBuffer: number[]): Promise<boolean> => {
    try {
      console.log('开始写入固件')
      const updateStatus = await this.deviceIns.upgrade(new Uint8Array(firmwareBuffer), (progress: UpgradeOptions) => this.handleUpgradeProgress(progress), { ...firmwareUpdateOptions })
      console.log('固件更新完成，结果:', updateStatus)
      return updateStatus
    } catch (error) {
      console.error('固件更新过程中发生错误:', error)
      return false
    }
  }

  async updateFirmware(firmwareBuffer: number[]): Promise<boolean> {
    if (!firmwareBuffer || firmwareBuffer.length === 0) return false
    let canWriteFirmware = false
    const usbChangeHandler = (data: any) =>
      this.handleUsbChange(data, () => {
        canWriteFirmware = true
      })
    try {
      const info = (await this.deviceIns.getDevicesInfo()) as jingtaiV2BaseInfo

      // 进入 Bootloader 模式
      if (await this.enterBootloaderIfNeeded(info)) {
        this.deviceIns.on('usbChange' as any, usbChangeHandler)
        while (!canWriteFirmware) await delay(200)
        this.deviceIns.off('usbChange' as any)
      } else {
        canWriteFirmware = true
      }

      if (canWriteFirmware) {
        const upgradeResult = await this.handlerUpgrade(firmwareBuffer)
        if (!upgradeResult) {
          console.error('Firmware upgrade failed during writing process.', upgradeResult)
          return false
        }
        await this.finishUpgrade()
      }
      console.log('升级完成!!!')
      this.isConnected = false
      return true
    } catch (error) {
      console.error('Firmware update failed:', error)
      return false
    }
  }
  isConnected = false
  private async handleUsbChange(data: any, onConnect: () => void) {
    if (data.type === 'connect' && !this.isConnected) {
      this.isConnected = true
      await delay(200)
      await this.deviceIns.getDevices()
      await this.deviceIns.init(this.id)
      onConnect()
    }
  }

  private async enterBootloaderIfNeeded(info: jingtaiV2BaseInfo): Promise<boolean> {
    if (info.runModeVersion === 0x00) {
      this.sendMessage({
        Current: 0,
        Total: 0,
        Status: FirmwareUpgradeStatus.AppToBoot,
      })
      await this.deviceIns.appToBoot()
      return true
    }
    return false
  }

  private async finishUpgrade() {
    this.sendMessage({
      Current: 100,
      Total: 100,
      Status: FirmwareUpgradeStatus.BootToApp,
    })
    await this.deviceIns.bootToApp()
  }
}
interface UpgradeOptions {
  current: number
  total: number
  percentage: number
  updateStatus?: string
  systemInfo?: object
}

const firmwareUpdateOptions = {
  writeDelay: 30, // 写入每块数据后的延迟（默认 30ms）
  toBootDelay: 4000, // 进入 Bootloader 后的延迟（默认 4000ms）
  toAppDelay: 4000, // 返回应用模式后的延迟（默认 4000ms）
}
